import React from 'react'
import { Text } from '@hub/text'
import { LocationDropPinMediumIcon } from '@hub/icon'
import { sortBy } from 'lodash'
import {
  Navigation,
  NavigationLink,
  NavigationPanel,
  NavigationPanelBody,
  NavigationPanelCloseButton,
  NavigationPanelHeader,
} from '@hub/navigation'
import { H4 } from '@hub/heading'
import { getAbsolutePathFromRouter } from '@scentregroup/shared/helpers/url-helpers'
import { Stack } from '@hub/stack'
import { Box } from '@hub/box'
import { useIsRouteChanging, useRouter } from '@scentregroup/shared/next/router'

type Centre = {
  slug: string
  title: string
  state: string
}

type CentreSelectorNavProps = {
  country: 'Australia' | 'New Zealand'
  centres: Centre[]
  open: boolean
  containerRef: React.RefObject<HTMLElement | null>
  onClose: () => void
}

export const CentreSelectorNav: React.FC<CentreSelectorNavProps> = ({
  centres,
  country,
  open,
  containerRef,
  onClose,
}) => {
  const isRouteChanging = useIsRouteChanging()
  const centresByState = React.useMemo(() => {
    const centresByStateUnsorted = centres
      .reduce<{ state: string; centres: Centre[] }[]>((acc, centre) => {
        const state = acc.find(({ state }) => centre.state === state)
        if (!state) {
          return acc.concat([{ state: centre.state, centres: [centre] }])
        }
        state.centres.push(centre)
        return acc
      }, [])
      .map(item => ({
        ...item,
        centres: sortBy(item.centres, 'title'),
      }))
    return sortBy(centresByStateUnsorted, 'state')
  }, [centres])
  const { basePath } = useRouter()
  return (
    <Navigation
      containerRef={containerRef}
      placement="left"
      allowOpen={!isRouteChanging}
      onClose={onClose}
      backgroundColor="surfaceBrandPrimary"
    >
      <NavigationPanel isOpen={open}>
        <NavigationPanelHeader>
          <NavigationPanelCloseButton />
        </NavigationPanelHeader>
        <NavigationPanelBody>
          <Stack gap="spacing-md">
            <Box>
              <H4 as={Text} sx={{ paddingBottom: 'spacing-lg' }}>
                Find a store within a centre
              </H4>
              <Text tone="strong">National</Text>
              <NavigationLink href="/stores">{country}</NavigationLink>
            </Box>
            {centresByState.map(({ state, centres }, key) => (
              <Box key={key}>
                <Text tone="strong">{state}</Text>
                {centres.map((centre, key) => (
                  <NavigationLink
                    key={key}
                    href={getAbsolutePathFromRouter({
                      route: '[centre]/browse-stores',
                      basePath,
                      query: { centre: centre.slug },
                    })}
                    icon={<LocationDropPinMediumIcon />}
                  >
                    {centre.title}
                  </NavigationLink>
                ))}
              </Box>
            ))}
          </Stack>
        </NavigationPanelBody>
      </NavigationPanel>
    </Navigation>
  )
}
